import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from "react";
import { Mail, ArrowLeft, Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogPortal,
} from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { TextField } from "./TextField";
import { PrimaryButton } from "./PrimaryButton";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { authService } from "@/services/authService";

interface ForgotPasswordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ModalStep = 'email' | 'code' | 'newPassword';

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export function ForgotPasswordModal({ open, onOpenChange }: ForgotPasswordModalProps) {
  useLanguage();
  const [email, setEmail] = useState("");
  const [emailValid, setEmailValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<ModalStep>('email');
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | undefined>();
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  // Validate email in real-time
  useEffect(() => {
    if (email.length === 0) {
      setEmailValid(null);
      setError(undefined);
    } else if (isValidEmail(email)) {
      setEmailValid(true);
      setError(undefined);
    } else {
      setEmailValid(false);
      setError(translateInterfaceText("Digite um e-mail válido"));
    }
  }, [email]);

  // Countdown timer for resend code
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (step === 'code' && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [step, resendTimer]);

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!emailValid) {
      setError(translateInterfaceText("Digite um e-mail válido"));
      return;
    }

    setIsLoading(true);
    setError(undefined);

    try {
      const response = await authService.sendEmailRecovery(email);

      if (response.data?.success) {
        toast.success(translateInterfaceText("Código enviado para seu e-mail!"));
        setStep('code');
      } else {
        toast.error(response.data?.message || translateInterfaceText("Erro ao enviar código de recuperação."));
      }
    } catch (error) {
      toast.error(translateInterfaceText("Erro ao enviar código de recuperação. Tente novamente."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 4) {
      setCodeError(translateInterfaceText("Digite o código de 4 dígitos"));
      return;
    }

    setIsLoading(true);
    setCodeError(undefined);

    try {
      // Validate code
      const response = await authService.validateCode(email, code);

      if (response.data?.success) {
        setStep('newPassword');
      } else {
        toast.error(response.data?.message || translateInterfaceText("Código inválido."));
      }
    } catch (error) {
      toast.error(translateInterfaceText("Erro ao validar código. Tente novamente."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;

    setIsLoading(true);

    try {
      const response = await authService.sendEmailRecovery(email);

      if (response.data?.success) {
        toast.success(translateInterfaceText("Código reenviado!"));
        setResendTimer(60);
        setCanResend(false);
      } else {
        toast.error(response.data?.message || translateInterfaceText("Erro ao reenviar código."));
      }
    } catch (error) {
      toast.error(translateInterfaceText("Erro ao reenviar código. Tente novamente."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword || newPassword.length < 6) {
      toast.error(translateInterfaceText("A senha deve ter pelo menos 6 caracteres."));
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.newPassword(email, newPassword, code);

      if (response.data?.success) {
        toast.success(translateInterfaceText("Senha alterada com sucesso! Faça login com sua nova senha."));
        handleClose();
      } else {
        toast.error(response.data?.message || translateInterfaceText("Erro ao alterar senha."));
      }
    } catch (error) {
      toast.error(translateInterfaceText("Erro ao alterar senha. Tente novamente."));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset state after modal closes
    setTimeout(() => {
      setEmail("");
      setCode("");
      setError(undefined);
      setCodeError(undefined);
      setEmailValid(null);
      setStep('email');
      setResendTimer(60);
      setCanResend(false);
    }, 200);
  };

  const handleBack = () => {
    if (step === 'newPassword') {
      setStep('code');
      setNewPassword("");
    } else if (step === 'code') {
      setStep('email');
      setCode("");
      setCodeError(undefined);
      setResendTimer(60);
      setCanResend(false);
    } else {
      handleClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-[520px] translate-x-[-50%] translate-y-[-50%] rounded-2xl bg-white p-6 sm:p-8 shadow-2xl border-0 duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
        >
          <div className="flex flex-col items-center">
            {/* Logo */}
            <img
              src="/assets/imgs/logo-azul-e-preta.png"
              alt={translateInterfaceText("HostVSL")}
              className="h-[38px] mb-6"
            />

            {step === 'email' && (
              <>
                {/* Title */}
                <h2
                  className="text-xl font-bold mb-2 text-center"
                  style={{ color: '#1E293B' }}
                >
                  {translateInterfaceText("Recuperar Acesso")}</h2>

                {/* Subtitle */}
                <p
                  className="text-sm text-center mb-6"
                  style={{ color: '#5B667A' }}
                >
                  {translateInterfaceText("Informe seu e-mail e enviaremos um código para redefinir sua senha.")}</p>

                {/* Form */}
                <form onSubmit={handleSubmitEmail} className="w-full space-y-5">
                  <TextField
                    label={translateInterfaceText("E-mail")}
                    type="email"
                    placeholder="seuemail@dominio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={error}
                    icon={<Mail className="h-4 w-4" />}
                    autoComplete="email"
                    rightElement={
                      emailValid === true ? (
                        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-[#1F6BFF]">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      ) : null
                    }
                  />

                  <PrimaryButton
                    type="submit"
                    loading={isLoading}
                    loadingText={translateInterfaceText("Enviando...")}
                    disabled={!emailValid}
                  >
                    {translateInterfaceText("Enviar código")}</PrimaryButton>
                </form>
              </>
            )}

            {step === 'code' && (
              <>
                {/* Title */}
                <h2
                  className="text-xl font-bold mb-2 text-center"
                  style={{ color: '#1E293B' }}
                >
                  {translateInterfaceText("Informe o código de confirmação")}</h2>

                {/* Subtitle */}
                <p
                  className="text-sm text-center mb-6"
                  style={{ color: '#5B667A' }}
                >
                  {translateInterfaceText("Enviamos um código de 4 dígitos para seu e-mail. Digite-o abaixo para continuar.")}</p>

                {/* OTP Form */}
                <form onSubmit={handleSubmitCode} className="w-full space-y-5">
                  <div className="flex flex-col items-center gap-3">
                    <InputOTP
                      maxLength={4}
                      value={code}
                      onChange={(value) => {
                        setCode(value);
                        if (codeError) setCodeError(undefined);
                      }}
                    >
                      <InputOTPGroup className="gap-2 sm:gap-3">
                        <InputOTPSlot
                          index={0}
                          className="w-12 h-12 sm:w-14 sm:h-14 text-lg sm:text-xl font-semibold rounded-xl border-2 border-gray-200 first:rounded-xl last:rounded-xl"
                        />
                        <InputOTPSlot
                          index={1}
                          className="w-12 h-12 sm:w-14 sm:h-14 text-lg sm:text-xl font-semibold rounded-xl border-2 border-gray-200 first:rounded-xl last:rounded-xl"
                        />
                        <InputOTPSlot
                          index={2}
                          className="w-12 h-12 sm:w-14 sm:h-14 text-lg sm:text-xl font-semibold rounded-xl border-2 border-gray-200 first:rounded-xl last:rounded-xl"
                        />
                        <InputOTPSlot
                          index={3}
                          className="w-12 h-12 sm:w-14 sm:h-14 text-lg sm:text-xl font-semibold rounded-xl border-2 border-gray-200 first:rounded-xl last:rounded-xl"
                        />
                      </InputOTPGroup>
                    </InputOTP>

                    {codeError && (
                      <p className="text-sm text-red-500">{codeError}</p>
                    )}

                    {/* Resend Timer */}
                    <div className="text-center mt-1">
                      {!canResend ? (
                        <p className="text-sm text-gray-500">
                          {translateInterfaceText("Reenviar código em")}{" "}<span className="font-semibold">{resendTimer}s</span>
                        </p>
                      ) : (
                        <button
                          type="button"
                          onClick={handleResendCode}
                          disabled={isLoading}
                          className="text-sm font-medium text-[#1F6BFF] hover:underline disabled:opacity-50"
                        >
                          {translateInterfaceText("Reenviar código")}</button>
                      )}
                    </div>
                  </div>

                  <PrimaryButton
                    type="submit"
                    loading={isLoading}
                    loadingText={translateInterfaceText("Verificando...")}
                    disabled={code.length !== 4}
                  >
                    {translateInterfaceText("Continuar")}</PrimaryButton>
                </form>
              </>
            )}

            {step === 'newPassword' && (
              <>
                <h2
                  className="text-xl font-bold mb-2 text-center"
                  style={{ color: '#1E293B' }}
                >
                  {translateInterfaceText("Criar nova senha")}</h2>

                <p
                  className="text-sm text-center mb-6"
                  style={{ color: '#5B667A' }}
                >
                  {translateInterfaceText("Digite sua nova senha para concluir a recuperação de acesso.")}</p>

                <form onSubmit={handleSubmitNewPassword} className="w-full space-y-5">
                  <TextField
                    label={translateInterfaceText("Nova senha")}
                    type="password"
                    placeholder={translateInterfaceText("Mínimo 6 caracteres")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />

                  <PrimaryButton
                    type="submit"
                    loading={isLoading}
                    loadingText={translateInterfaceText("Salvando...")}
                    disabled={newPassword.length < 6}
                  >
                    {translateInterfaceText("Salvar nova senha")}</PrimaryButton>
                </form>
              </>
            )}

            {/* Back Link */}
            <button
              type="button"
              onClick={handleBack}
              className="mt-4 flex items-center gap-2 text-sm font-medium transition-colors hover:underline"
              style={{ color: '#1F6BFF' }}
            >
              <ArrowLeft className="w-4 h-4" />
              {translateInterfaceText("Voltar para login")}</button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
